/**
 * F39 (Wave 2C) — admin "reject placement" modal.
 *
 * Mandatory reason (10–500 chars) → POST /admin/placements/:id/reject. The
 * server transitions the placement → rejected + refunds the consumed credit +
 * creates NO F23 line item, and audit-logs the reason (F06 onSend hook).
 * Gated server-side on `placement_approvals:approve`.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea } from '@bravemove/ui-web';
import { RejectPlacementInputSchema } from '@bravemove/types';

export interface RejectPlacementDialogProps {
  readonly open: boolean;
  readonly onConfirm: (reason: string) => Promise<void>;
  readonly onClose: () => void;
}

export function RejectPlacementDialog({ open, onConfirm, onClose }: RejectPlacementDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open]);

  const reasonTrimmed = reason.trim();
  const valid = RejectPlacementInputSchema.safeParse({ reason: reasonTrimmed }).success;

  const submit = async () => {
    setServerError(null);
    if (!valid) {
      setServerError(
        t('placement:admin.approvals.reject_invalid', {
          defaultValue: 'Provide a reason (10–500 characters).',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(reasonTrimmed);
      onClose();
    } catch {
      setServerError(
        t('placement:admin.inventory.error_generic', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('placement:admin.approvals.reject', { defaultValue: 'Reject placement' })}
      description={t('placement:admin.approvals.reject_description', {
        defaultValue:
          'Rejecting refunds any consumed credit and creates no charge. The reason is shared with the venue.',
      })}
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="reject-placement-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={submitting || !valid}
            data-testid="reject-placement-confirm"
          >
            {t('placement:admin.approvals.reject', { defaultValue: 'Reject' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        data-testid="reject-placement-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField
          label={t('placement:admin.approvals.reject_reason', {
            defaultValue: 'Reason for rejection',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="reject-placement-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        {serverError ? (
          <div data-testid="reject-placement-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default RejectPlacementDialog;
